import type { IntakeSpec, FurnitureShelfDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): FurnitureShelfDetails {
  return intake.details as FurnitureShelfDetails;
}

const PANEL_T = 18; // panel thickness in mm
const DEFAULT_SHELF_DEPTH = 300;
const DEFAULT_TOTAL_HEIGHT = 1800;
const DEFAULT_TOTAL_WIDTH = 800;

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const totalWidth = intake.core.size?.width ?? DEFAULT_TOTAL_WIDTH;
  const totalHeight = intake.core.size?.height ?? DEFAULT_TOTAL_HEIGHT;
  const shelfDepth = intake.core.size?.length ?? DEFAULT_SHELF_DEPTH;
  const shelfCount = d.shelfCount;

  const out: Part[] = [];

  // Corner shelf: replace 2 sides with 1 L-shaped custom stub
  if (d.shelfType === 'corner') {
    out.push({
      id: 'corner_frame',
      name: 'コーナーフレーム',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// corner shelf back, V2',
      boundingBox: { w: totalWidth, d: shelfDepth, h: totalHeight },
      position: [0, 0, totalHeight / 2],
      rotation: [0, 0, 0],
      tags: ['frame', 'corner'],
      paramRefs: [],
    });
  } else {
    // 2 side panels
    out.push(
      {
        id: 'side_left',
        name: '側板（左）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: totalHeight,
        depth: shelfDepth,
        thickness: PANEL_T,
        position: [-(totalWidth / 2), 0, totalHeight / 2],
        rotation: [0, 90, 0],
        tags: ['side', 'left'],
        paramRefs: [],
        cutouts: [],
      },
      {
        id: 'side_right',
        name: '側板（右）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: totalHeight,
        depth: shelfDepth,
        thickness: PANEL_T,
        position: [totalWidth / 2, 0, totalHeight / 2],
        rotation: [0, 90, 0],
        tags: ['side', 'right'],
        paramRefs: [],
        cutouts: [],
      },
    );
  }

  // Horizontal shelves
  const innerWidth = totalWidth - 2 * PANEL_T;

  // For wall_mounted: skip the bottom-most shelf
  const shelvesToPlace = d.shelfType === 'wall_mounted' ? shelfCount - 1 : shelfCount;
  const startShelf = d.shelfType === 'wall_mounted' ? 1 : 0;

  for (let i = 0; i < shelvesToPlace; i++) {
    const shelfIndex = startShelf + i;
    // Distribute evenly: divide totalHeight into (shelfCount+1) segments
    const zPos = ((shelfIndex + 1) / (shelfCount + 1)) * totalHeight;
    out.push({
      id: `shelf_${i + 1}`,
      name: `棚板 ${i + 1}`,
      kind: 'panel',
      materialId: 'mat-primary',
      width: innerWidth,
      depth: shelfDepth,
      thickness: PANEL_T,
      position: [0, 0, zPos],
      rotation: [0, 0, 0],
      tags: ['shelf'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Back panel
  if (d.hasBack) {
    out.push({
      id: 'back',
      name: '背板',
      kind: 'panel',
      materialId: 'mat-primary',
      width: totalWidth,
      depth: totalHeight,
      thickness: 6,
      position: [0, shelfDepth / 2, totalHeight / 2],
      rotation: [90, 0, 0],
      tags: ['back'],
      paramRefs: [],
      cutouts: [],
    });
  }

  // Wall mounting bracket stub for wall_mounted
  if (d.shelfType === 'wall_mounted') {
    out.push({
      id: 'wall_bracket',
      name: '壁取付金具',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// wall mounting bracket placeholder',
      boundingBox: { w: 100, d: 50, h: 50 },
      position: [0, -shelfDepth / 2, totalHeight - 50],
      rotation: [0, 0, 0],
      tags: ['bracket', 'wall_mount'],
      paramRefs: [],
    });
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  let joinery = d.joineryType;

  // cam_lock: no dedicated joint kind in B2 core / B2 rest. Treat as 'screw' for
  // V1 seed; agent can refine.
  if (joinery === 'cam_lock') joinery = 'screw';

  const totalWidth = intake.core.size?.width ?? DEFAULT_TOTAL_WIDTH;
  const totalHeight = intake.core.size?.height ?? DEFAULT_TOTAL_HEIGHT;
  const shelfDepth = intake.core.size?.length ?? DEFAULT_SHELF_DEPTH;
  const shelfCount = d.shelfCount;
  const shelvesToPlace = d.shelfType === 'wall_mounted' ? shelfCount - 1 : shelfCount;
  const innerWidth = totalWidth - 2 * PANEL_T;

  const out: Joint[] = [];

  // shelf→side joints (each shelf connects to left and right side)
  for (let i = 0; i < shelvesToPlace; i++) {
    const shelfId = `shelf_${i + 1}`;

    const makeJoint = (jId: string, sideId: string): Joint => {
      if (joinery === 'dado') {
        return { id: jId, fromPartId: shelfId, toPartId: sideId, type: 'dado', width: PANEL_T, depth: 6, length: innerWidth };
      } else if (joinery === 'dowel') {
        return { id: jId, fromPartId: shelfId, toPartId: sideId, type: 'dowel', diameter: 8, length: 30, count: 4 };
      } else if (joinery === 'biscuit') {
        return { id: jId, fromPartId: shelfId, toPartId: sideId, type: 'biscuit', size: '20', count: 4 };
      } else {
        // screw (default, also covers former cam_lock)
        return { id: jId, fromPartId: shelfId, toPartId: sideId, type: 'screw', size: 'M5', length: 30, count: 4 };
      }
    };

    if (d.shelfType !== 'corner') {
      out.push(makeJoint(`shelf_${i + 1}_to_left`, 'side_left'));
      out.push(makeJoint(`shelf_${i + 1}_to_right`, 'side_right'));
    }
  }

  // Back panel joints
  if (d.hasBack) {
    if (joinery === 'dado' || joinery === 'screw') {
      // dado-for-back-panel needs `rabbet` joint kind, deferred to B2 rest.
      // V1 emits screw joints for back-to-side; agent can refine.
      //
      out.push(
        { id: 'back_to_left', fromPartId: 'back', toPartId: 'side_left', type: 'screw', size: 'M5', length: 30, count: 4 },
        { id: 'back_to_right', fromPartId: 'back', toPartId: 'side_right', type: 'screw', size: 'M5', length: 30, count: 4 },
      );
    }
  }

  // Suppress unused variable warnings
  void shelfDepth;
  void totalHeight;

  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const shelfCount = d.shelfCount;
  const shelvesToPlace = d.shelfType === 'wall_mounted' ? shelfCount - 1 : shelfCount;

  const out: Feature[] = [];

  // Edge chamfer on each shelf
  for (let i = 0; i < shelvesToPlace; i++) {
    out.push({
      id: `chamfer_shelf_${i + 1}`,
      partId: `shelf_${i + 1}`,
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 1,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  // Edge chamfer on each side panel
  if (d.shelfType !== 'corner') {
    out.push(
      {
        id: 'chamfer_side_left',
        partId: 'side_left',
        kind: 'edge_treatment',
        treatment: 'chamfer',
        amount: 1,
        scope: 'all',
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        paramRefs: [],
      },
      {
        id: 'chamfer_side_right',
        partId: 'side_right',
        kind: 'edge_treatment',
        treatment: 'chamfer',
        amount: 1,
        scope: 'all',
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        paramRefs: [],
      },
    );
  }

  return out;
}
